import { useState } from "react"
import { Music, Plus, RefreshCw, Wand2, Library } from "lucide-react"

import { Button } from "@aix/ui"
import { trpc } from "../../lib/trpc"

import { SongLibrary } from "./components/SongLibrary"
import { UploadModal } from "./components/UploadModal"
import type { FilterState } from "./components/SongFilters"
import { SongFilters } from "./components/SongFilters"
import { MashupBuilder } from "./components/MashupBuilder"

type ViewMode = "library" | "builder"

const DEFAULT_FILTERS: FilterState = {
  status: undefined,
  genre: undefined,
  bpmMin: undefined,
  bpmMax: undefined,
  search: "",
}

export default function MashupScreen() {
  const [viewMode, setViewMode] = useState<ViewMode>("library")
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTERS)

  // Fetch songs with filters
  const {
    data: songsData,
    isLoading,
    refetch,
  } = trpc.mashupSong.list.useQuery({
    limit: 50,
    offset: 0,
    status: filters.status as any,
    genre: filters.genre as any,
    bpmMin: filters.bpmMin,
    bpmMax: filters.bpmMax,
    search: filters.search || undefined,
  })

  const handleUploadComplete = () => {
    setIsUploadModalOpen(false)
    void refetch()
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="container mx-auto px-4 sm:px-6 py-4">
          {/* Mobile: Stack, Desktop: Row */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            {/* Title */}
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Music className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold">DJ Mashup Tool</h1>
                <p className="text-xs sm:text-sm text-muted-foreground hidden sm:block">
                  Upload songs, separate stems, create mashups
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 justify-between sm:justify-end">
              {/* View Toggle */}
              <div className="flex rounded-lg border border-border overflow-hidden flex-1 sm:flex-none">
                <button
                  onClick={() => setViewMode("library")}
                  className={`flex-1 sm:flex-none px-3 py-1.5 text-sm flex items-center justify-center gap-1.5 ${
                    viewMode === "library" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-accent"
                  }`}
                >
                  <Library className="h-4 w-4" />
                  <span className="hidden xs:inline">Library</span>
                </button>
                <button
                  onClick={() => setViewMode("builder")}
                  className={`flex-1 sm:flex-none px-3 py-1.5 text-sm flex items-center justify-center gap-1.5 ${
                    viewMode === "builder" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-accent"
                  }`}
                >
                  <Wand2 className="h-4 w-4" />
                  <span className="hidden xs:inline">Create</span>
                </button>
              </div>

              {viewMode === "library" && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => refetch()}
                    disabled={isLoading}
                    className="hidden sm:flex"
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? "animate-spin" : ""}`} />
                    Refresh
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => refetch()}
                    disabled={isLoading}
                    className="flex sm:hidden"
                  >
                    <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
                  </Button>
                  <Button onClick={() => setIsUploadModalOpen(true)} size="sm" className="sm:size-default">
                    <Plus className="h-4 w-4 sm:mr-2" />
                    <span className="hidden sm:inline">Upload Song</span>
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {viewMode === "library" ? (
        <>
          {/* Filters */}
          <div className="container mx-auto px-4 sm:px-6 py-4">
            <SongFilters filters={filters} onFiltersChange={setFilters} />
          </div>

          {/* Song Library */}
          <div className="container mx-auto px-4 sm:px-6 pb-8">
            <SongLibrary songs={songsData?.songs || []} isLoading={isLoading} onRefresh={refetch} />

            {/* Stats */}
            {songsData && (
              <div className="mt-4 text-xs sm:text-sm text-muted-foreground">
                Showing {songsData.songs.length} of {songsData.total} songs
                {songsData.hasMore && " (scroll to load more)"}
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="container mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <MashupBuilder
            onBack={() => setViewMode("library")}
            onCreateProject={(projectId) => {
              // TODO: Navigate to project detail page
              console.log("Created project:", projectId)
              setViewMode("library")
            }}
          />
        </div>
      )}

      {/* Upload Modal */}
      <UploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUploadComplete={handleUploadComplete}
      />
    </div>
  )
}
